import { useState } from "react";

import {
  AlertCircle,
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Sparkles,
} from "lucide-react";

import { ReportQASectionEvaluation, ReportQASectionStatus } from "@api/reportQa";

import { cn } from "@utils/ui";

/**
 * One row in the Report QA panel — covers a single template section.
 *
 * All cards start collapsed so the result reads as a scannable list;
 * the user opens the cards they care about.
 */
type Props = {
  evaluation: ReportQASectionEvaluation;
  index: number;
};

const SUGGESTIONS_VISIBLE_BY_DEFAULT = 1;

const STATUS_META: Record<
  ReportQASectionStatus,
  {
    Icon: typeof Check;
    iconClass: string;
    badgeLabel: string;
    badgeClass: string;
  }
> = {
  strong: {
    Icon: Check,
    iconClass: "text-success",
    badgeLabel: "Strong",
    badgeClass: "text-success",
  },
  weak: {
    Icon: AlertTriangle,
    iconClass: "text-warning",
    badgeLabel: "Weak",
    badgeClass: "text-warning",
  },
  missing: {
    Icon: AlertCircle,
    iconClass: "text-destructive",
    badgeLabel: "Missing",
    badgeClass: "text-destructive",
  },
};

export const SectionCard = ({ evaluation, index }: Props) => {
  const meta = STATUS_META[evaluation.status];
  const suggestions = evaluation.suggestions ?? [];
  const [isOpen, setIsOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const visibleSuggestions = showAll
    ? suggestions
    : suggestions.slice(0, SUGGESTIONS_VISIBLE_BY_DEFAULT);
  const hiddenCount = suggestions.length - visibleSuggestions.length;
  const canToggleAll = suggestions.length > SUGGESTIONS_VISIBLE_BY_DEFAULT;

  const { Icon } = meta;

  return (
    <div className="rounded-lg border border-slate-200 bg-background-white shadow-sm dark:border-dark-50">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
        className="flex w-full items-start gap-2 px-3 py-3 text-left"
      >
        <Icon className={cn("mt-0.5 size-4 shrink-0", meta.iconClass)} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <span className="text-sm font-semibold text-[#1A1939]">
              {index}. {evaluation.name}
            </span>
            <span
              className={cn(
                "whitespace-nowrap text-[12px] font-semibold leading-none tracking-[0.06px]",
                meta.badgeClass
              )}
            >
              {meta.badgeLabel}
            </span>
          </div>
          {evaluation.note && (
            <p className="mt-1 text-[12px] leading-[18px] text-[#0B0B18]">
              {evaluation.note}
            </p>
          )}
        </div>
        {isOpen ? (
          <ChevronUp className="mt-0.5 size-4 shrink-0 text-[#1A1939]" />
        ) : (
          <ChevronDown className="mt-0.5 size-4 shrink-0 text-[#1A1939]" />
        )}
      </button>

      {isOpen && suggestions.length > 0 && (
        <div className="border-t border-slate-200 px-3 py-3 dark:border-dark-50">
          <div className="mb-2 flex items-center gap-1 text-[10px] font-semibold leading-none text-accent">
            <Sparkles className="size-3" />
            <span>Suggestions ({suggestions.length})</span>
          </div>
          <ul className="flex flex-col gap-2">
            {visibleSuggestions.map((suggestion, index) => (
              <li
                key={index}
                className="flex flex-col gap-0.5 rounded bg-[#F3F5FB] p-2 dark:bg-slate-800/50"
              >
                <p className="text-[12px] font-normal leading-[18px] text-[#0B0B18]">
                  {suggestion.text}
                </p>
                {suggestion.learn_more_url && (
                  <a
                    href={suggestion.learn_more_url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1 text-[10px] font-normal leading-none tracking-[0.03px] text-[#0066CC] hover:underline"
                  >
                    Learn more
                    <ExternalLink className="size-3" />
                  </a>
                )}
              </li>
            ))}
          </ul>
          {canToggleAll && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="mt-2 inline-flex items-center gap-1 text-[10px] font-medium leading-[18px] text-[#0066CC] underline"
            >
              {showAll ? "Show less" : "Show all"}
              {showAll ? (
                <ChevronUp className="size-3" />
              ) : (
                <ChevronDown className="size-3" />
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
